import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchHighScores, fetchScoreAdapter } from "../lib/api";

const PERIODS: { key: string; label: string }[] = [
  { key: "daily", label: "Daily" },
  { key: "monthly", label: "This month" },
  { key: "all", label: "All-time" },
];

// The Daily / Monthly / All-time leaderboard panel — reimplemented in React
// (the old mountPanel mutated the DOM). Score *submission* still flows through
// the un-bundled highscores.js capture path, which dispatches
// "rearcade:score-recorded" on success; we invalidate on that to refresh.
export function GameScores({ gameId }: { gameId: string }) {
  const [period, setPeriod] = useState("daily");
  const qc = useQueryClient();

  const adapterQ = useQuery({ queryKey: ["score-adapter", gameId], queryFn: () => fetchScoreAdapter(gameId) });
  const scorable =
    !!adapterQ.data && adapterQ.data.capture !== "none" && adapterQ.data.capture !== "unsupported";

  const scoresQ = useQuery({
    queryKey: ["highscores", gameId, period],
    queryFn: () => fetchHighScores(gameId, period),
    enabled: scorable,
  });

  useEffect(() => {
    const onRecorded = (e: Event) => {
      if ((e as CustomEvent).detail?.game === gameId) {
        void qc.invalidateQueries({ queryKey: ["highscores", gameId] });
      }
    };
    window.addEventListener("rearcade:score-recorded", onRecorded as EventListener);
    return () => window.removeEventListener("rearcade:score-recorded", onRecorded as EventListener);
  }, [gameId, qc]);

  if (adapterQ.isLoading || !scorable) return null;

  const rows = scoresQ.data?.scores ?? [];
  return (
    <section className="notes-scores" aria-labelledby="hs-heading">
      <h2 id="hs-heading">High scores</h2>
      <div className="score-tabs" role="tablist" aria-label="High score period">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            type="button"
            role="tab"
            className={period === p.key ? "active" : undefined}
            aria-selected={period === p.key}
            onClick={() => setPeriod(p.key)}
          >
            {p.label}
          </button>
        ))}
      </div>
      <ol className="score-table">
        {scoresQ.isError ? (
          <li className="score-empty">leaderboard offline — start the scoring server</li>
        ) : rows.length ? (
          rows.map((r) => (
            <li key={`${r.rank}-${r.name}`}>
              <span className="score-rank">{r.rank}</span>
              <span className="score-name">{r.name}</span>
              <span className="score-pts">{r.score.toLocaleString("en-US")}</span>
            </li>
          ))
        ) : (
          <li className="score-empty">no scores yet — be the first</li>
        )}
      </ol>
    </section>
  );
}
